import { isFailedTransaction } from '../transactions';

export const ADDRESS_VALIDATION_ERROR_CODE = 'VET360_AV_ERROR';

export const EDIT_VIEW_UPDATE_EFFECTS = {
  FOCUS_CANCEL_BUTTON: 'FOCUS_CANCEL_BUTTON',
  FOCUS_FIRST_FORM_ELEMENT: 'FOCUS_FIRST_FORM_ELEMENT',
  FOCUS_ERROR_CLOSE_BUTTON: 'FOCUS_ERROR_CLOSE_BUTTON',
  OPEN_ADDRESS_VALIDATION_MODAL: 'OPEN_ADDRESS_VALIDATION_MODAL',
};

export const hasAddressValidationError = transactionRequest =>
  transactionRequest?.error?.errors?.some(
    e => e.code === ADDRESS_VALIDATION_ERROR_CODE,
  ) || false;

const hasVisibleError = ({ transaction, transactionRequest }) =>
  !!transactionRequest?.error || isFailedTransaction(transaction);

/**
 * The edit view's update logic as data. Given the previous and current view
 * state it returns the ordered list of side effects to run — no dispatching,
 * no DOM access, no timers — so every branch can be asserted on without
 * rendering anything.
 *
 * `prev` and `next` are both shaped:
 *   { field, shouldFocusCancelButton, transaction, transactionRequest }
 *
 * Transaction polling is not here; it lives in useTransactionPolling.
 */
export const getEditViewUpdateEffects = ({ prev, next }) => {
  const effects = [];

  // Return focus to the cancel button when the confirm cancel modal closes.
  if (!prev.shouldFocusCancelButton && next.shouldFocusCancelButton) {
    effects.push({ type: EDIT_VIEW_UPDATE_EFFECTS.FOCUS_CANCEL_BUTTON });
  }

  // The form renders nothing until the field lands in the store, so first
  // focus has to wait for it to appear.
  if (!prev.field && !!next.field) {
    effects.push({ type: EDIT_VIEW_UPDATE_EFFECTS.FOCUS_FIRST_FORM_ELEMENT });
  }

  const isAddressValidationError = hasAddressValidationError(
    next.transactionRequest,
  );

  // PORT NOTE (bug fix): the class opened the address validation view when an
  // AV error arrived and `!prevProps.transactionRequest?.error`. Any earlier
  // error of any kind — a failed save the Veteran then corrected — satisfied
  // that guard for the wrong reason and suppressed the modal. Comparing the
  // AV-error state instead of "was there any error" only triggers on a new AV
  // error, which is what the comment on the class's guard described.
  if (
    isAddressValidationError &&
    !hasAddressValidationError(prev.transactionRequest)
  ) {
    effects.push({
      type: EDIT_VIEW_UPDATE_EFFECTS.OPEN_ADDRESS_VALIDATION_MODAL,
    });
  }

  // PORT NOTE (bug fix): the class ran this on every update where an error was
  // present, not only when the error appeared, so each keystroke after a failed
  // save pulled focus off the input and back onto the alert's close button.
  // componentDidUpdate re-ran on every render, which is what made it repeat.
  // The trade-off of the edge trigger: an error already in the store when the
  // edit view opens no longer takes focus, because no new alert appeared for
  // the Veteran to be sent to.
  if (
    !isAddressValidationError &&
    hasVisibleError(next) &&
    !hasVisibleError(prev)
  ) {
    effects.push({ type: EDIT_VIEW_UPDATE_EFFECTS.FOCUS_ERROR_CLOSE_BUTTON });
  }

  return effects;
};
